import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Rótulo do último trecho do breadcrumb, publicado pela própria tela.
 *
 * Problema que resolve: o breadcrumb é montado a partir da URL, então
 * `/importacao/68` mostrava **`#68`** — o id do banco, que não significa nada
 * para quem usa o sistema. O identificador de negócio é `10006/26`.
 *
 * Só a tela sabe qual é esse identificador, porque ele vem dos dados carregados.
 * Então a tela publica e o shell exibe. Vale para todos os módulos:
 * processo, empresa, AWB, estimativa.
 *
 * Uso na tela:
 *
 *     useRotuloRota(form.numero_processo)   // "10006/26"
 *     useRotuloRota(empresa?.razao_social)  // "NOW ELETROPOSTOS LTDA"
 *
 * Passar `null`/`undefined` mantém o comportamento padrão (o id), o que é o certo
 * enquanto os dados estão carregando — melhor que piscar vazio.
 */

interface Ctx {
  rotulo: string | null
  definir: (v: string | null) => void
}

const RotuloCtx = createContext<Ctx | null>(null)

export function RotuloRotaProvider({ children }: { children: ReactNode }) {
  const [rotulo, setRotulo] = useState<string | null>(null)
  const definir = useCallback((v: string | null) => setRotulo(v), [])
  const valor = useMemo(() => ({ rotulo, definir }), [rotulo, definir])
  return <RotuloCtx.Provider value={valor}>{children}</RotuloCtx.Provider>
}

/** Usado pelo shell para exibir. */
export function useRotuloAtual(): string | null {
  return useContext(RotuloCtx)?.rotulo ?? null
}

/**
 * Usado pelas telas para publicar o identificador de negócio.
 * Limpa ao desmontar, para não vazar o rótulo de uma tela na seguinte.
 */
export function useRotuloRota(valor: string | null | undefined) {
  const ctx = useContext(RotuloCtx)

  useEffect(() => {
    if (!ctx) return
    ctx.definir(valor?.trim() ? valor : null)
    return () => ctx.definir(null)
  }, [ctx, valor])
}
